/**
 * Validation utilities for QR code data and configurations
 */

/**
 * URL validation regex
 */
const URL_REGEX = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;

/**
 * Email validation regex
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Phone validation regex (international format)
 */
const PHONE_REGEX = /^\+?[\d\s-]{10,}$/;

/**
 * Validates a URL string
 * @param {string} url - URL to validate
 * @returns {boolean}
 */
export function isValidURL(url) {
  try {
    new URL(url);
    return true;
  } catch {
    // Try with https:// prefix
    try {
      new URL(`https://${url}`);
      return URL_REGEX.test(url);
    } catch {
      return false;
    }
  }
}

/**
 * Validates an email address
 * @param {string} email - Email to validate
 * @returns {boolean}
 */
export function isValidEmail(email) {
  return EMAIL_REGEX.test(email);
}

/**
 * Validates a phone number
 * @param {string} phone - Phone number to validate
 * @returns {boolean}
 */
export function isValidPhone(phone) {
  return PHONE_REGEX.test(phone.replace(/[\s-]/g, ''));
}

/**
 * Validates WiFi configuration string
 * @param {string} wifi - WiFi string
 * @returns {boolean}
 */
export function isValidWiFi(wifi) {
  return wifi.startsWith('WIFI:') && wifi.includes('S:') && wifi.endsWith(';;');
}

/**
 * Validates vCard string
 * @param {string} vcard - vCard string
 * @returns {boolean}
 */
export function isValidVCard(vcard) {
  return vcard.startsWith('BEGIN:VCARD') && vcard.includes('END:VCARD');
}

/**
 * Detects the type of QR data
 * @param {string} data - Data to analyze
 * @returns {string} - Data type
 */
export function detectDataType(data) {
  if (!data) return 'empty';
  
  const trimmed = data.trim();
  
  if (isValidURL(trimmed)) return 'url';
  if (trimmed.startsWith('mailto:') || isValidEmail(trimmed)) return 'email';
  if (trimmed.startsWith('tel:') || isValidPhone(trimmed)) return 'phone';
  if (trimmed.startsWith('sms:')) return 'sms';
  if (isValidWiFi(trimmed)) return 'wifi';
  if (isValidVCard(trimmed)) return 'vcard';
  if (trimmed.startsWith('geo:')) return 'location';
  if (trimmed.startsWith('MECARD:')) return 'mecard';
  
  return 'text';
}

/**
 * Validates QR data and returns validation result
 * @param {string} data - Data to validate
 * @returns {Object} - Validation result
 */
export function validateQRData(data) {
  if (!data || data.trim() === '') {
    return {
      valid: false,
      type: 'empty',
      message: 'Please enter content for your QR code',
    };
  }

  const maxLength = 2953;
  if (data.length > maxLength) {
    return {
      valid: false,
      type: 'too-long',
      message: `Content exceeds maximum length of ${maxLength} characters`,
    };
  }

  const type = detectDataType(data);
  
  return {
    valid: true,
    type,
    message: `Valid ${type} content detected`,
  };
}

/**
 * Validates hex color string
 * @param {string} color - Color to validate
 * @returns {boolean}
 */
export function isValidHexColor(color) {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}

/**
 * Validates QR configuration object
 * @param {Object} config - Configuration to validate
 * @returns {Object} - Validation result with errors
 */
export function validateQRConfig(config) {
  const errors = {};

  if (!config.data || config.data.trim() === '') {
    errors.data = 'Content is required';
  }

  if (config.width && (config.width < 100 || config.width > 2000)) {
    errors.width = 'Width must be between 100 and 2000 pixels';
  }

  if (config.height && (config.height < 100 || config.height > 2000)) {
    errors.height = 'Height must be between 100 and 2000 pixels';
  }

  if (config.margin && (config.margin < 0 || config.margin > 100)) {
    errors.margin = 'Margin must be between 0 and 100';
  }

  if (config.dotsColor && !isValidHexColor(config.dotsColor)) {
    errors.dotsColor = 'Invalid color format';
  }

  if (config.backgroundColor && !isValidHexColor(config.backgroundColor)) {
    errors.backgroundColor = 'Invalid color format';
  }

  if (config.imageSize && (config.imageSize < 0.1 || config.imageSize > 0.5)) {
    errors.imageSize = 'Logo size must be between 10% and 50%';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Calculates if QR will be scannable with current settings
 * Returns a confidence score (0-100)
 * @param {Object} config - QR configuration
 * @returns {Object} - Scannability assessment
 */
export function assessScannability(config) {
  let score = 100;
  const warnings = [];
  const suggestions = [];

  // Check contrast between dots and background
  const dotsColor = config.dotsColor || '#000000';
  const bgColor = config.backgroundColor || '#ffffff';
  
  const contrast = calculateContrast(dotsColor, bgColor);
  if (contrast < 4.5) {
    score -= 30;
    warnings.push('Low contrast between QR code and background');
    suggestions.push('Increase contrast for better scanning');
  }

  // Check if logo is too large
  if (config.image && config.imageSize > 0.4) {
    score -= 20;
    warnings.push('Logo size may affect scanning reliability');
    suggestions.push('Consider reducing logo size to 30% or less');
  }

  // Check error correction level with logo
  if (config.image && config.errorCorrectionLevel !== 'H') {
    score -= 15;
    warnings.push('Higher error correction recommended with logo');
    suggestions.push('Use "High" error correction when adding a logo');
  }

  // Check margin
  if (config.margin < 5) {
    score -= 10;
    warnings.push('Small margin may cause scanning issues');
    suggestions.push('Increase margin to at least 10px');
  }

  // Assess overall quality
  let quality = 'excellent';
  if (score < 90) quality = 'good';
  if (score < 70) quality = 'fair';
  if (score < 50) quality = 'poor';

  return {
    score: Math.max(0, score),
    quality,
    warnings,
    suggestions,
  };
}

/**
 * Calculate contrast ratio between two colors
 * @param {string} color1 - First hex color
 * @param {string} color2 - Second hex color
 * @returns {number} - Contrast ratio
 */
function calculateContrast(color1, color2) {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Calculate relative luminance of a color
 * @param {string} hex - Hex color string
 * @returns {number} - Luminance value
 */
function getLuminance(hex) {
  const rgb = hex.replace('#', '').match(/.{2}/g)
    .map(x => parseInt(x, 16) / 255)
    .map(x => x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4));
  
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

export default {
  isValidURL,
  isValidEmail,
  isValidPhone,
  isValidWiFi,
  isValidVCard,
  detectDataType,
  validateQRData,
  isValidHexColor,
  validateQRConfig,
  assessScannability,
};
